// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
'use client';

import * as React from 'react';

import {
	Box,
	Button,
	Center,
	FileUpload as ChakraFileUpload,
	Icon,
	IconButton,
	Image,
	Span,
	Text,
	useFileUploadContext,
	useRecipe,
} from '@chakra-ui/react';
import type { BoxProps, ButtonProps, RecipeProps } from '@chakra-ui/react';
import { LuFile, LuUpload, LuX } from 'react-icons/lu';

function renderPreviewImage(file: File, callbackFn: (result: string) => void) {
	const reader = new FileReader();
	reader.onloadend = () => callbackFn(reader.result as string);
	reader.readAsDataURL(file);
}

export type FileUploadRootProps = {
	inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
} & ChakraFileUpload.RootProps;

export const FileUploadRoot = React.forwardRef<
	HTMLInputElement,
	FileUploadRootProps
>((props, ref) => {
	const { children, inputProps, ...rest } = props;

	return (
		<ChakraFileUpload.Root {...rest}>
			<ChakraFileUpload.HiddenInput ref={ref} {...inputProps} />
			{children}
		</ChakraFileUpload.Root>
	);
});

export type FileUploadDropzoneProps = {
	label:        React.ReactNode;
	description?: React.ReactNode;
} & ChakraFileUpload.DropzoneProps;

export const FileUploadDropzone = React.forwardRef<
	HTMLInputElement,
	FileUploadDropzoneProps
>((props, ref) => {
	const {
		children, label, description, ...rest
	} = props;

	return (
		<ChakraFileUpload.Dropzone ref={ref} {...rest}>
			<Icon color={'fg.muted'} fontSize={'xl'}>
				<LuUpload />
			</Icon>
			<ChakraFileUpload.DropzoneContent>
				<div>{label}</div>
				{description && <Text color={'fg.muted'}>{description}</Text>}
			</ChakraFileUpload.DropzoneContent>
			{children}
		</ChakraFileUpload.Dropzone>
	);
});

type VisibilityProps = {
	showSize?:  boolean;
	clearable?: boolean;
};

type FileUploadItemProps = {
	file: File;
} & VisibilityProps;

const FileUploadItem = React.forwardRef<HTMLLIElement, FileUploadItemProps>(
	(props, ref) => {
		const { file, showSize, clearable } = props;

		return (
			<ChakraFileUpload.Item ref={ref} file={file}>
				<ChakraFileUpload.ItemPreview asChild>
					<Icon color={'fg.muted'} fontSize={'lg'}>
						<LuFile />
					</Icon>
				</ChakraFileUpload.ItemPreview>
				{showSize
					? (
						<ChakraFileUpload.ItemContent>
							<ChakraFileUpload.ItemName />
							<ChakraFileUpload.ItemSizeText />
						</ChakraFileUpload.ItemContent>
					)
					:           <ChakraFileUpload.ItemName flex={'1'} />}
				{clearable && (
					<ChakraFileUpload.ItemDeleteTrigger asChild>
						<IconButton color={'fg.muted'} size={'xs'} variant={'ghost'}>
							<LuX />
						</IconButton>
					</ChakraFileUpload.ItemDeleteTrigger>
				)}
			</ChakraFileUpload.Item>
		);
	},
);

type FileUploadListProps = {
	files?: File[];
} & VisibilityProps & ChakraFileUpload.ItemGroupProps;

export const FileUploadList = React.forwardRef<
	HTMLUListElement,
	FileUploadListProps
>((props, ref) => {
	const {
		showSize, clearable, files, ...rest
	} = props;

	const fileUpload = useFileUploadContext();
	const acceptedFiles = files ?? fileUpload.acceptedFiles;

	if (acceptedFiles.length === 0) return null;

	return (
		<ChakraFileUpload.ItemGroup ref={ref} {...rest}>
			{acceptedFiles.map(file => (
				<FileUploadItem
					key={file.name}
					clearable={clearable}
					file={file}
					showSize={showSize}
				/>
			))}
		</ChakraFileUpload.ItemGroup>
	);
});

// ! TODO upse NextImage and Adjust and seperate into another component
export const ImageUpload = (props: BoxProps) => {
	const [ previewUrl, setPreviewUrl ] = React.useState<string | undefined>(undefined);
	const { acceptedFiles, openFilePicker, deleteFile } = useFileUploadContext();
	const isFileExist = acceptedFiles.length > 0;
	const [ file ] = acceptedFiles;

	if (isFileExist) renderPreviewImage(file!, setPreviewUrl);

	const handleDeleteFile = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		deleteFile(file!);
	};

	return (
		<Box
			border={isFileExist ? '1px solid' : '1px dashed'}
			borderColor={isFileExist ? 'blackAlpha.400' : 'blackAlpha.900'}
			borderRadius={'xl'}
			cursor={'pointer'}
			p={1}
			pos={'relative'}
			onClick={openFilePicker}
			{...props}
		>
			{isFileExist
				? (
					<React.Fragment>
						<Image
							alt={file?.name ?? ''}
							h={'100%'}
							objectFit={'contain'}
							src={previewUrl}
							w={'100%'}
						/>
						<IconButton
							opacity={0.7}
							position={'absolute'}
							right={'0'}
							size={'xs'}
							top={'0'}
							onClick={handleDeleteFile}
						>
							<LuX />
						</IconButton>
					</React.Fragment>
				)
				: (
					<Center h={'100%'} w={'100%'}>
						<Icon color={'fg.muted'} fontSize={'xl'}>
							<LuUpload />
						</Icon>
					</Center>
				)}
		</Box>
	);
};

type ImageUploadItemProps = {
	file: File;
} & VisibilityProps;

const ImageUploadItem = React.forwardRef<HTMLLIElement, ImageUploadItemProps>(
	(props, ref) => {
		const [ previewUrl, setPreviewUrl ] = React.useState<string | undefined>(undefined);
		const { file } = props;
		renderPreviewImage(file, setPreviewUrl);

		return (
			<ChakraFileUpload.Item
				ref={ref}
				file={file}
				h={'100%'}
				position={'relative'}
				w={'100%'}
			>
				<ChakraFileUpload.ItemPreview asChild>
					<Image
						alt={file.name}
						h={'100%'}
						objectFit={'contain'}
						src={previewUrl}
						w={'100%'}
					/>
				</ChakraFileUpload.ItemPreview>
				<ChakraFileUpload.ItemDeleteTrigger asChild>
					<IconButton
						color={'fg.muted'}
						position={'absolute'}
						right={'0'}
						size={'xs'}
						top={'0'}
						variant={'ghost'}
					>
						<LuX />
					</IconButton>
				</ChakraFileUpload.ItemDeleteTrigger>
			</ChakraFileUpload.Item>
		);
	},
);

type ImageUploadListProps = {
	files?: File[];
} & VisibilityProps & ChakraFileUpload.ItemGroupProps;

export const ImageUploadList = React.forwardRef<
	HTMLUListElement,
	ImageUploadListProps
>((props, ref) => {
	const {
		showSize, clearable, files, ...rest
	} = props;

	const fileUpload = useFileUploadContext();
	const acceptedFiles = files ?? fileUpload.acceptedFiles;

	if (acceptedFiles.length === 0) return null;

	return (
		<ChakraFileUpload.ItemGroup ref={ref} {...rest}>
			{acceptedFiles.map(file => (
				<ImageUploadItem
					key={file.name}
					clearable={clearable}
					file={file}
					showSize={showSize}
				/>
			))}
		</ChakraFileUpload.ItemGroup>
	);
});

type Assign<T, U> = Omit<T, keyof U> & U;

type FileInputProps = {
	placeholder?: React.ReactNode;
} & Assign<ButtonProps, RecipeProps<'input'>>;

export const FileInput: React.ForwardRefExoticComponent<
	FileInputProps & React.RefAttributes<HTMLButtonElement>
> = React.forwardRef<HTMLButtonElement, FileInputProps>(
	(props, ref) => {
		const inputRecipe = useRecipe({ key: 'input' });
		const [ recipeProps, restProps ] = inputRecipe.splitVariantProps(props);
		const { placeholder = 'Select file(s)', ...rest } = restProps;

		return (
			<ChakraFileUpload.Trigger asChild>
				<Button
					ref={ref}
					unstyled
					py={'0'}
					{...rest}
					css={[ inputRecipe(recipeProps), props.css ]}
				>
					<ChakraFileUpload.Context>
						{({ acceptedFiles }) => {
							if (acceptedFiles.length === 1) {
								return <span>{acceptedFiles?.[0]?.name}</span>;
							}

							if (acceptedFiles.length > 1) {
								return <span>{acceptedFiles.length} files</span>;
							}

							return <Span color={'fg.subtle'}>{placeholder}</Span>;
						}}
					</ChakraFileUpload.Context>
				</Button>
			</ChakraFileUpload.Trigger>
		);
	},
);

ImageUpload.displayName = 'ImageUpload';
ImageUploadItem.displayName = 'ImageUploadItem';
ImageUploadList.displayName = 'ImageUploadList';
FileUploadRoot.displayName = 'FileUploadRoot';
FileUploadDropzone.displayName = 'FileUploadDropzone';
FileUploadList.displayName = 'FileUploadList';
FileInput.displayName = 'FileInput';
FileUploadItem.displayName = 'FileUploadItem';

export const FileUploadLabel = ChakraFileUpload.Label;
export const FileUploadClearTrigger = ChakraFileUpload.ClearTrigger;
export const FileUploadTrigger = ChakraFileUpload.Trigger;
